/**
 * Public izleme sayfası — oturumsuz, salt okunur.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * KABUK YOK, GEZİNME YOK, "GİRİŞ YAP" YOK.
 *
 * Bu sayfayı açan kişi bizim kullanıcımız değil: bir müşteri, bir yüklenici,
 * belki telefonuna gelen bağlantıya tıklayan bir saha sorumlusu. Ona alt sekme
 * çubuğu, profil ve yapılandırma sekmeleri göstermek, gidemeyeceği kapıları
 * göstermek olurdu.
 *
 * Görev 4'ün dürüstlük kuralları BURADA DA geçerli — hatta daha önemli, çünkü
 * bu sayfayı açan kişinin sistemi sorgulayacak başka bir yolu yok:
 *   · ölçümün YAŞI değerin yanında
 *   · ölçüm yoksa "%0" değil "ölçüm yok"
 *   · "canlı" iddiası YOK (bu sayfada akış da yok)
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { VeriDurumu } from '@gasco/shared';
import { dolulukKg } from '@gasco/shared';

import { ApiError, istek } from '../lib/api.ts';
import { Badge } from '../design/Status.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { DURUM_SUNUMU, gecenSure } from '../field/okuma.ts';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';

interface PaylasimTanki {
  readonly tank_id: number;
  readonly tank_number: number | null;
  readonly capacity_m3: number;
  readonly nominal_kg: number | null;
  readonly level_pct: number | null;
  readonly pressure_bar: number | null;
  readonly read_at: string | null;
}

interface PaylasimSahasi {
  readonly site_id: number;
  readonly name: string;
  readonly city: string | null;
  readonly data_state: VeriDurumu;
  readonly last_ok_at: string | null;
  readonly tanks: readonly PaylasimTanki[];
}

interface PaylasimCevabi {
  readonly label: string | null;
  readonly sites: readonly PaylasimSahasi[];
  readonly now: string;
}

export function PublicIzleme(): React.JSX.Element {
  const { token } = useParams({ from: '/izleme/$token' });

  const sorgu = useQuery({
    queryKey: ['public-izleme', token],
    queryFn: () => istek<PaylasimCevabi>(`/public/izleme/${token}`),
    staleTime: 30_000,
    /*
     * 4xx TEKRARLANMIYOR (`query.ts` kuralı) — geçersiz linkte üç kez
     * denemenin faydası yok. Ama bu sayfada ek bir sebep var: her deneme
     * public uçtaki hız sınırından bir hak yiyor.
     */
  });

  return (
    <div className="min-h-dvh bg-surface-sunken">
      <header className="border-b border-border bg-surface px-4 py-4">
        <h1 className="text-lg font-semibold text-ink">{sorgu.data?.label ?? 'Tank izleme'}</h1>
        <p className="mt-0.5 text-sm text-ink-muted">Gasco SCADA — paylaşılan görünüm</p>
      </header>

      <main className="p-4">
        {sorgu.isPending && <SkeletonList rows={3} />}

        {sorgu.isError && (
          <EmptyState
            title={
              sorgu.error instanceof ApiError && sorgu.error.status === 404
                ? 'Bu bağlantı geçerli değil'
                : 'Veriler yüklenemedi'
            }
            /*
             * 404'ün üç sebebi var (yok · süresi geçmiş · iptal edilmiş) ve
             * sunucu ÜÇÜNÜ DE aynı cevapla veriyor: hangisi olduğunu söylemek
             * linkin var olduğunu doğrular. Ekran da ayırmıyor; kullanıcıya
             * yapabileceği tek şey söyleniyor.
             */
            description={
              sorgu.error instanceof ApiError && sorgu.error.status === 404
                ? 'Bağlantının süresi dolmuş ya da iptal edilmiş olabilir. Bağlantıyı paylaşan kişiden yenisini isteyin.'
                : 'Lütfen daha sonra tekrar deneyin.'
            }
          />
        )}

        {sorgu.data !== undefined && (
          <>
            {sorgu.data.sites.length === 0 ? (
              <EmptyState
                title="Gösterilecek saha yok"
                description="Bu bağlantı için henüz saha tanımlanmamış."
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {sorgu.data.sites.map((saha) => (
                  <li key={saha.site_id}>
                    <SahaKarti saha={saha} simdi={sorgu.data.now} />
                  </li>
                ))}
              </ul>
            )}

            {/*
             * Sayfanın kendisi ne kadar tazeydi — kullanıcı sekmeyi açık
             * bıraktıysa bu damga eskiyecek ve ölçüm yaşları da onunla
             * hesaplandığı için tutarlı kalacak.
             */}
            <p className="mt-4 text-center text-xs text-ink-muted">
              Veriler{' '}
              {new Date(sorgu.data.now).toLocaleString('tr-TR', {
                timeZone: 'Europe/Istanbul',
              })}{' '}
              itibarıyla · sayfayı yenileyerek güncelleyebilirsiniz
            </p>
          </>
        )}
      </main>
    </div>
  );
}

function SahaKarti({
  saha,
  simdi,
}: {
  readonly saha: PaylasimSahasi;
  readonly simdi: string;
}): React.JSX.Element {
  const durum = DURUM_SUNUMU[saha.data_state];
  const yas = gecenSure(saha.last_ok_at, simdi);

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-base font-semibold text-ink">{saha.name}</p>
            {saha.city !== null && saha.city !== '' && (
              <p className="mt-0.5 text-sm text-ink-muted">{saha.city}</p>
            )}
          </div>
          <Badge tone={durum.tone}>{durum.etiket}</Badge>
        </div>

        <p className="mt-2 text-sm text-ink-muted">
          {yas === null ? 'Henüz ölçüm alınmadı' : `Son veri: ${yas}`}
        </p>

        {saha.tanks.length > 0 && (
          <ul className="mt-3 flex flex-col gap-3">
            {saha.tanks.map((tank) => {
              const kg = dolulukKg(tank.level_pct, tank.nominal_kg);
              const olcumYasi = gecenSure(tank.read_at, simdi);
              return (
                <li key={tank.tank_id}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-ink-muted">
                      Tank {tank.tank_number ?? '—'} · {tank.capacity_m3} m³
                    </span>
                    <span className="font-medium text-ink">
                      {tank.level_pct === null ? (
                        // Boş tank ile ölçümsüz tank aynı şey değil.
                        'ölçüm yok'
                      ) : (
                        <>
                          %{tank.level_pct.toFixed(1)}
                          {kg !== null && ` · ${kg} kg`}
                        </>
                      )}
                    </span>
                  </div>
                  {tank.level_pct !== null && (
                    <div
                      className="mt-1 h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
                      aria-hidden="true"
                    >
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{ width: `${String(Math.min(Math.max(tank.level_pct, 0), 100))}%` }}
                      />
                    </div>
                  )}
                  {olcumYasi !== null && (
                    <p className="mt-1 text-xs text-ink-muted">Ölçüm: {olcumYasi}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
